import type { ClientLogo, IndustrySummary, Testimonial } from '@/types'

export const clients: Record<string, ClientLogo> = {
    banxa: { name: 'Banxa', src: '/images/clients/banxa.png', width: 400 },
    incomm: { name: 'InComm Payments', src: '/images/clients/incomm.png', width: 400 },
    paybis: { name: 'Paybis', src: '/images/clients/paybis.png', width: 400 },
    gateio: { name: 'Gate.io', src: '/images/clients/gate-io.png', width: 400 },
    coinflow: { name: 'Coinflow', src: '/images/clients/coinflow.png', width: 400 },
    eneba: { name: 'Eneba', src: '/images/clients/eneba.png', width: 400 },
    becharge: { name: 'beCharge', src: '/images/clients/becharge.png', width: 400 },
    zeusx: { name: 'ZeusX', src: '/images/clients/zeusx.png', width: 400 },
    btcc: { name: 'BTCC', src: '/images/clients/btcc.png', width: 400 },
    totus: { name: 'Totus', src: '/images/clients/totus.png', width: 400 },
    eldorado: { name: 'Eldorado', src: '/images/clients/eldorado.png', width: 400 },
    driffle: { name: 'Driffle', src: '/images/clients/driffle.png', width: 400 },
    bigkid: { name: 'Bigkid Gaming', src: '/images/clients/bigkid-gaming.png', width: 400 },
    coingate: { name: 'CoinGate', src: '/images/clients/coingate.png', width: 400 }
}

export const homeClients: ClientLogo[] = [
    clients.banxa,
    clients.incomm,
    clients.paybis,
    clients.gateio,
    clients.coinflow,
    clients.eneba,
    clients.becharge,
    clients.zeusx,
    clients.btcc,
    clients.totus
]

export const guarantees = [
    {
        index: '01',
        value: '98%',
        qualifier: 'Up to',
        title: 'High Approval',
        text: 'Transaction approval rates up to 98%. Guaranteed.'
    },
    {
        index: '02',
        value: '100%',
        qualifier: 'Liability',
        title: 'Chargeback Guarantee',
        text: '100% liability for fraud- and service-related chargebacks.'
    },
    {
        index: '03',
        value: '50bps',
        qualifier: 'No higher than',
        title: 'Low Chargebacks',
        text: 'Fraud-related chargebacks no higher than 50bps.'
    },
    {
        index: '04',
        value: '500ms',
        qualifier: 'Under',
        title: 'Realtime Decisioning',
        text: 'Full decisions to Approve or Decline a transaction in under 500ms.'
    }
]

export const mustHaves = [
    {
        title: 'Adaptive AI for Superior Accuracy',
        text: 'nSure.ai provides a dedicated model for each customer, training payment fraud on your data, and uses Adaptive AI to provide the highest level of accuracy in the industry.'
    },
    {
        title: 'Payment Fraud Decisions in Under Half a Second',
        text: 'Certainty scores aren’t enough. nSure.ai’s payment fraud prevention solution analyzes hundreds of thousands of data points to return full payment fraud decisions in 500 ms or less.'
    },
    {
        title: 'Industry-Proven Insights',
        text: 'nSure.ai’s payment fraud prevention solutions are sculpted by industry veterans with first-hand experience facing the unique challenges merchants encounter every day.'
    },
    {
        title: 'Guaranteed Net Incremental Profit',
        text: 'Payment fraud prevention solutions from nSure.ai deliver a guaranteed net incremental profit to our growing network of clients.'
    },
    {
        title: '100% Liability for Chargeback Costs',
        text: 'nSure.ai takes 100% liability for all fraud- and service-related chargebacks.'
    },
    {
        title: '24/7 White Glove Support and Guidance',
        text: 'Our fraud prevention experts work hand-in-hand with your team to provide technical support and guide your various payment fraud strategies for each of your segments.'
    },
    {
        title: 'Dedicated API and SDK',
        text: 'nSure.ai delivers to each customer a dedicated API documentation based on your very specific purchase flow, that allows you to deploy in 15 days.'
    },
    {
        title: 'Don’t Lose Customers to Verification',
        text: 'Verification tools are not payment fraud prevention tools. Reduce customer verification to the strict minimum required and clawback those conversions.'
    }
]

export const testimonials: Testimonial[] = [
    {
        name: 'Karl Denzer',
        role: 'SVP, Emerging Technologies',
        company: 'InComm Payments',
        logo: '/images/clients/incomm.png',
        metric: '+10%',
        metricLabel: 'US sales in six months',
        quote: [
            'In just six months with nSure.ai, we’ve lifted our US sales by more than 10% while eliminating manual review and fraud risk.'
        ]
    },
    {
        name: 'Innokenty Isers',
        role: 'Founder & CEO',
        company: 'Paybis.com',
        logo: '/images/clients/paybis.png',
        metric: '1:18',
        metricLabel: 'Average onboarding, KYC included',
        quote: [
            'Clocking in at an average of 1 minute and 18 seconds, our customer onboarding time might just set an industry speed record! And yes, that includes KYC verification.',
            'Curious to know our secret sauce for this Formula-1-worthy onboarding? It’s our collaboration with nSure.ai. With them, we’ve pioneered one of the industry’s fastest and most efficient customer verification processes.'
        ]
    },
    {
        name: 'Daniel Lev',
        role: 'CEO & Co-Founder',
        company: 'Coinflow Labs',
        logo: '/images/clients/coinflow.png',
        metric: '19×',
        metricLabel: 'Overall processing since going live',
        quote: [
            'nSure.ai made our integration fast and seamless. We were fully onboarded in just 85 days, with the technical integration taking less than two weeks. Their support was exceptional, even hosting our CTO in their TLV office to ensure everything went smoothly.',
            'Since going live, our monthly GMV has grown nearly 5x, with overall processing up by over 19x. Their service is invaluable, and we’re thrilled with the results.'
        ]
    },
    {
        name: 'Mantas Eitutis',
        role: 'Head of Payments and Risk',
        company: 'Eneba',
        logo: '/images/clients/eneba.png',
        metric: 'Eneba',
        metricLabel: 'Higher approval rates for new users',
        quote: [
            'nSure.ai’s unique offering in payment fraud prevention has been a game-changer for Eneba. Their adaptive AI solution has increased our approval rates for new users. Their expertise and customer service dedication make nSure.ai a significant strategic partner, and an extension of our team.'
        ]
    },
    {
        name: 'Arminas Simkus',
        role: 'Payments Product Manager',
        company: 'Eldorado',
        logo: '/images/clients/eldorado.png',
        metric: '0',
        metricLabel: 'Manual reviews within six months',
        quote: [
            'Partnering with nSure.ai has been transformative for Eldorado. Within the first six months, we saw higher revenue and streamlined our operations by eliminating manual reviews. The adaptive AI engine from nSure.ai continuously optimizes performance, ensuring impressive approval accuracy across our entire transaction flow.',
            'Their proactive approach and unwavering support allow us to focus on delivering exceptional service to our customers. Ultimately, nSure.ai has become an essential part of our success story as we drive growth and build trust together.'
        ]
    }
]

export const industries: IndustrySummary[] = [
    {
        slug: 'crypto',
        index: '01',
        name: 'Crypto',
        shortName: 'Crypto',
        tone: 'crypto',
        tagline: 'Streamline your fiat-to-crypto onramp.'
    },
    {
        slug: 'gaming',
        index: '02',
        name: 'Gaming',
        shortName: 'Gaming',
        tone: 'gaming',
        tagline: 'Don’t let caution keep you out of the game.'
    },
    {
        slug: 'prepaid-cards',
        index: '03',
        name: 'Prepaid & Gift Cards',
        shortName: 'Prepaid',
        tone: 'prepaid',
        tagline: 'Peace of mind is its own gift.'
    }
]

export const backers = [
    { name: 'Phoenix', role: 'Backer' },
    { name: 'Kamet', role: 'Backer' },
    { name: 'Lloyds', role: 'Stop-loss policy' }
]

export const departments = [
    'Revenue',
    'Engineering',
    'Data',
    'Service Logic',
    'Human Resource',
    'Fraud Operations',
    'Marketing',
    'Product'
]

export const countries = [
    'United States',
    'Canada',
    'Brazil',
    'Mexico',
    'United Kingdom',
    'Germany',
    'France',
    'Netherlands',
    'Sweden',
    'Switzerland',
    'Spain',
    'Italy',
    'Poland',
    'Portugal',
    'Denmark',
    'Norway',
    'Finland',
    'Belgium',
    'Austria',
    'Czech Republic',
    'Israel',
    'UAE',
    'Saudi Arabia',
    'South Africa',
    'Singapore',
    'Australia',
    'Japan',
    'South Korea',
    'India',
    'Hong Kong',
    'New Zealand',
    'Other'
]

export const industryOptions = [
    'Crypto Exchanges & Wallets',
    'Digital Assets',
    'Remittances',
    'Finance/Fintech',
    'Gaming/Video Gaming',
    'iGaming',
    'Prepaid/Gift Cards',
    'Media',
    'Other'
]
